<template>
  <div class="Ecosystem">
    <Loading
      v-if="bootStatus === 'pending'"
      variant="section"
      message="Loading feed…"
    />

    <div v-else-if="bootStatus === 'error'" class="bootError" role="alert">
      <h3>We couldn't load your feed</h3>
      <p>Please check your connection and try again.</p>
      <UnitButton variant="secondary" @click="initializeData">
        Try again
      </UnitButton>
    </div>

    <div v-else class="fullWidth">
      <div v-if="!hasMemberships" class="fullWidth">
        <CreateOrJoin />
      </div>
      <div v-else>
        <div v-if="user.loggedIn" class="column">
          <div>
            <!-- Mount MainMarketplace immediately; Masonry will use timeline-only when showItems='organisation' -->
            <MainMarketplace
              ref="mainMarketplaceRef"
              :products="products"
              :services="services"
              :buyerOrgHasCoins="buyerOrgHasCoins"
              :buyerHasOrganisations="buyerHasOrganisations"
              :organisationUid="
                showOwnOrganisationOnly ? currentUserOrgUID : null
              "
              :showItems="showOwnOrganisationOnly ? 'organisation' : 'allUsers'"
              :title="'Recently added'"
              :showSellingAction="showSellingAction"
              @noCoinsInBuyerOrg="handleNoCoinsInGrandparent"
              @hasCoinsInBuyerOrg="handleHasCoinsInGrandparent"
              @userHasNoOrganisations="handleUserHasNoOrganisations"
              @userHasOrganisations="handleUserHasOrganisations"
              @itemEdited="handleItemEdited"
            >
              <template #post-action>
                <div
                  v-if="
                    !isItemAuthorityReadsFeatureEnabled ||
                    canUsePrivateItemPublishing
                  "
                  class="row noGap"
                  style="gap: var(--space-2)"
                >
                  <UnitButton
                    variant="secondary"
                    class="ecosystem-post-action"
                    @click.stop="toggleSellingAction"
                    :class="{ pressed: showSellingAction }"
                    v-haptic
                    aria-label="Commit a product or service"
                    :aria-pressed="showSellingAction ? 'true' : 'false'"
                  >
                    <template #leading>
                      <svg class="compactImg" aria-hidden="true">
                        <use
                          href="@/assets/icons/iconset.svg#care_compact"
                        ></use>
                      </svg>
                    </template>
                    <span class="postBtnLabel">Commit</span>
                  </UnitButton>
                  <UnitButton
                    v-if="buyerHasOrganisations"
                    variant="secondary"
                    class="ecosystem-post-action"
                    @click.stop="handlePostClick"
                    v-haptic
                    aria-label="Create a post"
                  >
                    <template #leading>
                      <svg class="compactImg" aria-hidden="true">
                        <use
                          href="@/assets/icons/iconset.svg#megaphone_compact"
                        ></use>
                      </svg>
                    </template>
                    <span class="postBtnLabel">Post</span>
                  </UnitButton>
                </div>
              </template>
              <template #selling-action>
                <PrivateItemComposer
                  v-if="canUsePrivateItemPublishing && currentUserOrgUID"
                  :org-id="currentUserOrgUID"
                  :org-name="truncatedOrgName"
                  :show-dropdown="showSellingAction"
                  @close-selling-action="handleCloseSellingAction"
                  @item-published="handleItemEdited"
                />
                <SellingAction
                  ref="sellingActionRef"
                  v-if="buyerHasOrganisations"
                  :media-only="isItemAuthorityReadsFeatureEnabled"
                  :userCredentials="userCredentials"
                  :user="user"
                  :userUnitsOrg="userUnits_customOrgs"
                  :userIsInAnOrg="buyerHasOrganisations"
                  :showDropdown="showSellingAction"
                  @itemEdited="handleItemEdited"
                  @closeSellingAction="handleCloseSellingAction"
                  parentContext="Ecosystem"
                />
              </template>
            </MainMarketplace>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useStore } from "vuex";
import { computed, ref, onMounted } from "vue";

import MainMarketplace from "@/components/DashboardComponents/EcosystemComponents/MainMarketplace.vue";
import Loading from "@/components/GenericComponents/LoadingState.vue";
import CreateOrJoin from "@/components/LandingComponents/CreateOrJoin.vue";
import SellingAction from "@/components/DashboardComponents/EcosystemComponents/SellingAction.vue";
import PrivateItemComposer from "@/components/DashboardComponents/EcosystemComponents/PrivateItemComposer.vue";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";
import { beginBootStep } from "@/utils/bootDiagnostics";
import { withUiDeadline } from "@/utils/uiDeadline";
import { walletSummaryService } from "@/services/walletSummaryService";
import {
  isItemAuthorityCommandsFeatureEnabled,
  isItemAuthorityReadsFeatureEnabled,
  isOrgContextFeatureEnabled,
  isStorageAuthorityCommandsFeatureEnabled,
} from "@/config/runtimeConfig";

export default {
  name: "Ecosystem",
  components: {
    MainMarketplace,
    Loading,
    CreateOrJoin,
    SellingAction,
    PrivateItemComposer,
    UnitButton,
  },

  setup() {
    const store = useStore();
    const products = computed(() =>
      isItemAuthorityReadsFeatureEnabled ? [] : store.getters["products"]
    );
    const services = computed(() =>
      isItemAuthorityReadsFeatureEnabled ? [] : store.getters["services"]
    );
    const buyerOrgHasCoins = ref(false);
    const buyerHasOrganisations = ref(false);
    const bootStatus = ref("pending");
    const bootGeneration = ref(0);
    const showOwnOrganisationOnly = computed(
      () => store.getters["UIState/showOwnOrganisationOnly"]
    );
    // Get the organization name and logo
    const orgName = computed(() => {
      if (!currentUserOrgUID.value || !userUnits_customOrgs.value) return null;
      return userUnits_customOrgs.value[currentUserOrgUID.value]?.name || null;
    });

    const orgLogo = computed(() => {
      if (!currentUserOrgUID.value || !userUnits_customOrgs.value) return null;
      return userUnits_customOrgs.value[currentUserOrgUID.value]?.logo || null;
    });

    // Check if the current user's organization allows trades
    const isTradeAllowed = computed(
      () => store.getters["UIState/isTradeAllowed"]
    );

    const sellingActionRef = ref(null);
    const mainMarketplaceRef = ref(null);
    const showSellingAction = ref(false);
    const canUsePrivateItemPublishing =
      isItemAuthorityReadsFeatureEnabled &&
      isItemAuthorityCommandsFeatureEnabled &&
      isStorageAuthorityCommandsFeatureEnabled;

    const toggleSellingAction = () => {
      showSellingAction.value = !showSellingAction.value;
      console.log(
        "Ecosystem: Toggled showSellingAction to",
        showSellingAction.value
      );
    };
    const handleCloseSellingAction = () => {
      showSellingAction.value = false;
    };

    const handlePostClick = () => {
      showSellingAction.value = false;
      if (sellingActionRef.value) {
        sellingActionRef.value.openMediaOverlay();
      }
    };

    const currentUserOrgUID = computed(
      () => store.getters.selectedOrgUID || store.getters.currentOrgUID
    );

    const orgHasProducts = computed(() => {
      if (!currentUserOrgUID.value) return false;
      return products.value.some(
        (product) => product.organisation === currentUserOrgUID.value
      );
    });

    const orgHasServices = computed(() => {
      if (!currentUserOrgUID.value) return false;
      return services.value.some(
        (service) => service.organisation === currentUserOrgUID.value
      );
    });

    const truncatedOrgName = computed(() => {
      const orgName =
        userUnits_customOrgs.value?.[currentUserOrgUID.value]?.name ||
        "your organisation";
      if (orgName.length > 16) {
        return `${orgName.slice(0, 16)}...${orgName.slice(-3)}`;
      }
      return orgName;
    });

    const user = computed(() => store.getters.user);
    const userCredentials = computed(() => store.getters.userCredentials);
    const userUnits_customOrgs = computed(
      () => store.getters.selectedCustomOrgProjection
    );
    const hasMemberships = computed(
      () => (store.getters.userOrgIds || []).length > 0
    );

    const initializeData = async () => {
      const generation = ++bootGeneration.value;
      bootStatus.value = "pending";
      const finishMemberships = beginBootStep("memberships");
      try {
        const userUID =
          userCredentials.value?.data?.uid || user.value?.data?.uid;
        if (!userUID) throw new Error("No authenticated user found.");
        if (isOrgContextFeatureEnabled) {
          const orgUID = await store.dispatch("ensureSelectedOrg", { userUID });
          if (generation !== bootGeneration.value) return;
          buyerHasOrganisations.value = hasMemberships.value;
          finishMemberships({
            count: (store.getters.userOrgIds || []).length,
          });
          if (generation === bootGeneration.value) bootStatus.value = "ready";

          // Balance is optional presentation and must never delay the Feed.
          if (orgUID) {
            walletSummaryService.getOwnWalletSummary(orgUID)
              .then((summary) => {
                if (generation !== bootGeneration.value) return;
                buyerOrgHasCoins.value =
                  summary.walletStatus === "ready" &&
                  summary.balances !== null &&
                  BigInt(summary.balances.totalAvailableAtomic) > 0n;
              })
              .catch(() => {
                if (generation === bootGeneration.value) {
                  buyerOrgHasCoins.value = false;
                }
              });
          }
          return;
        }
        const orgUID = await withUiDeadline(
          store.dispatch("ensureSelectedOrg", { userUID }),
          // A bounded account can require five ten-row authority pages. The
          // normal 15-second UI deadline is too short for that complete,
          // sequentially cursor-bound bootstrap on a cold Function instance.
          { phase: "memberships", timeoutMs: 60_000 }
        );
        if (generation !== bootGeneration.value) return;
        finishMemberships({
          count: Object.keys(store.getters.userUnitsData || {}).length,
        });
        buyerHasOrganisations.value = !!orgUID;

        if (orgUID) {
          const finishEconomy = beginBootStep("economy");
          await withUiDeadline(
            store.dispatch("UIState/fetchEconomyRestrictions", {
              userUID,
              orgUID,
            }),
            { phase: "economy" }
          );
          finishEconomy({ outcome: "ready" });
          if (!store.getters["UIState/isTradeAllowed"]) {
            store.dispatch("UIState/setShowOwnOrganisationOnly", true);
          }

          // Non-critical enrichment starts after the marketplace can mount.
          store
            .dispatch("fetchAndCommitSelectedCustomOrg", {
              userId: userUID,
              orgUID,
            })
            .catch((error) =>
              console.warn("Expanded organisation data was not loaded:", error)
            );
        }
        if (generation === bootGeneration.value) bootStatus.value = "ready";
      } catch (error) {
        finishMemberships({ errorCode: error.code || "unknown" });
        if (generation === bootGeneration.value) bootStatus.value = "error";
      }
    };

    onMounted(initializeData);

    const handleNoCoinsInGrandparent = () => {
      buyerOrgHasCoins.value = false;
    };

    const handleUserHasNoOrganisations = () => {
      buyerHasOrganisations.value = false;
    };

    const handleUserHasOrganisations = () => {
      buyerHasOrganisations.value = true;
    };

    const handleHasCoinsInGrandparent = () => {
      buyerOrgHasCoins.value = true;
    };

    const handleItemEdited = () => {
      mainMarketplaceRef.value?.refreshTimeline?.({ reason: "item-edited" });
    };

    return {
      user,
      userCredentials,
      userUnits_customOrgs,
      products,
      services,
      buyerOrgHasCoins,
      buyerHasOrganisations,
      handleNoCoinsInGrandparent,
      handleHasCoinsInGrandparent,
      handleUserHasNoOrganisations,
      handleUserHasOrganisations,
      handleItemEdited,
      bootStatus,
      hasMemberships,
      initializeData,
      currentUserOrgUID,
      truncatedOrgName,
      showSellingAction,
      orgHasProducts,
      orgHasServices,
      showOwnOrganisationOnly,
      handleCloseSellingAction,
      handlePostClick,
      toggleSellingAction,
      sellingActionRef,
      mainMarketplaceRef,
      orgName,
      orgLogo,
      isTradeAllowed,
      isItemAuthorityReadsFeatureEnabled,
      canUsePrivateItemPublishing,
    };
  },
};
</script>
<style scoped>
.relativePosition {
  position: relative !important;
}

.ecosystem-post-action.pressed {
  box-shadow: var(--shadow-surface-recessed-compact);
}

.ecosystem-post-action.unit-button {
  height: var(--control-height-default);
  padding: 0 var(--space-6);
  gap: var(--space-2);
  border-color: var(--color-border-control);
  font-weight: var(--font-weight-normal);
}

.ecosystem-post-action.unit-button:not(:disabled):hover {
  background: transparent;
  transform: translateY(var(--motion-pressed-offset));
}

.marketSelector {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.marketSelector:hover {
  opacity: 0.8;
}

.org-logo {
  width: 31px;
  height: 31px;
  border-radius: var(--radius-sm);
  object-fit: cover;
}

/* Make the toggle unclickable when trade is not allowed */
.marketSelector.disabled {
  cursor: default;
  opacity: 0.55;
}
</style>
